import React from 'react';
import { Flex, Box } from 'rebass';
import { Formik, FormikConfig } from 'formik';
import {
  ButtonLarge,
  LoginLink,
  TermsAndConditions,
  FormHeader,
  FormSubHeader,
  ErrorNotification,
  EmailField,
  FirstNameField,
  LastNameField,
  WorkZipField,
  CheckBox,
} from '@mdlinx/component-library';
import * as Yup from 'yup';

import { FormContainer } from 'app/components/layout/form-container';

type DetailedFromValues = {
  email: string;
  firstName: string;
  lastName: string;
  postalCode: string;
  selfReportedUsmd: boolean;
};

export type SignUpFormDetailedProps = {
  onSubmit: FormikConfig<DetailedFromValues>['onSubmit'];
  errorMessage?: string;
  header: string;
  subHeader: string;
  initialFormValues?: DetailedFromValues;
  onLoginLinkClicked: () => void;
  disableEmailField?: boolean;
};

export const SignUpFormDetailed = ({
  onSubmit,
  errorMessage,
  header,
  subHeader,
  initialFormValues,
  onLoginLinkClicked,
}: SignUpFormDetailedProps) => {
  const defaultValues = {
    firstName: '',
    lastName: '',
    email: '',
    postalCode: '',
    selfReportedUsmd: true,
  };
  const initialValues = initialFormValues ? initialFormValues : defaultValues;
  const submitStarted = React.useRef(false);

  return (
    <FormContainer>
      <FormHeader>{header}</FormHeader>
      <Box mt="10px" />
      <FormSubHeader>{subHeader}</FormSubHeader>
      <Box mt="20px" />
      {errorMessage && (
        <Box my="12px">
          <ErrorNotification errorMessage={errorMessage} />
        </Box>
      )}
      <Box>
        <Formik
          initialValues={initialValues}
          validationSchema={Yup.object({
            firstName: Yup.string().required('Required'),
            lastName: Yup.string().required('Required'),
            email: Yup.string()
              .email('Invalid email address')
              .required('Required'),
            postalCode: Yup.string()
              .min(5, 'Invalid zip code')
              .required('Required'),
          })}
          onSubmit={onSubmit}
        >
          {formik => {
            const handleSubmitButtonClick = (event: React.SyntheticEvent) => {
              event.preventDefault();
              event.stopPropagation();
              submitStarted.current = true;
              formik.handleSubmit();
            };

            return (
              <form
                onSubmit={(event: React.FormEvent<HTMLFormElement>) => {
                  submitStarted.current = true;
                  formik.handleSubmit(event);
                }}
              >
                <EmailField submitStarted={submitStarted} />
                <Flex flexDirection={['column', 'row']}>
                  <FirstNameField />
                  <Box mx="5px" />
                  <LastNameField />
                </Flex>
                <FormSubHeader fontSize="14px" lineHeight={1.29}>
                  For Health Care Professionals please use your <b>work zip</b> code.
                </FormSubHeader>
                <Box width={['100%', '150px']} mt="5px">
                  <WorkZipField />
                </Box>
                <Box alignSelf="flex-start">
                  <CheckBox name="selfReportedUsmd" lightLabel label="I am a US doctor" />
                </Box>
                <Box mt="16px" mb="20px">
                  <ButtonLarge
                    type="submit"
                    disabled={!(formik.dirty && formik.isValid)}
                    isLoading={formik.isSubmitting}
                    onClick={handleSubmitButtonClick}
                  >
                    Sign up
                  </ButtonLarge>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Box>
      <Flex justifyContent="center">
        <Box my="20px" mx="auto">
          <LoginLink onLoginLinkClicked={onLoginLinkClicked} />
        </Box>
      </Flex>
      <Flex justifyContent="center">
        <TermsAndConditions />
      </Flex>
    </FormContainer>
  );
};
